import { format } from "date-fns";
import { isNullOrUndefined } from "@utils/common";
import { sleep } from "@utils/sleep";
import { budgetApi, budgetApiV2 } from "@/api";
import {
  SavingsPlanDataProviderType,
  TransactionType,
  type CustomerPreferenceType,
  type IAcquireConsentRequest,
  type IArchiveExpensePlanRequest,
  type IArchiveSavingsPlanRequest,
  type IAvailableBanks,
  type IBankAccountsFilterResponse,
  type IBudgetBalanceStatistics,
  type IBudgetBalanceStatisticsRequest,
  type IBudgetFinanceBreakdown,
  type IBudgetFinanceBreakdownRequest,
  type IBudgetMetadata,
  type IBudgetNewAccounts,
  type IBudgetServiceMetadata,
  type IBudgetSettings,
  type IBudgetSummary,
  type IBudgetTransactionAvailableCategories,
  type IBudgetTransactionAvailableCategoriesRequest,
  type IBudgetTransactionMetadata,
  type IBudgetTransactionMetadataRequest,
  type IBudgetTransactions,
  type IBudgetTransactionsRequest,
  type IBudgetWeeklyExpenseStatistics,
  type IBudgetWeeklyExpenseStatisticsRequest,
  type ICreateExpensePlanRequest,
  type ICreateTransactionCategoryRequest,
  type ICustomerBudgetPreferences,
  type IDetailedBalanceStatistics,
  type IDetailedBalanceStatisticsRequest,
  type IEditCustomTransactionCategoryRequest,
  type IEditExpensePlanRequest,
  type IEditTransactionCategoryRequest,
  type IExpenseComparisonStatistics,
  type IExpenseComparisonStatisticsExpenseCategories,
  type IExpenseComparisonStatisticsPartners,
  type IExpenseComparisonStatisticsPartnersRequest,
  type IExpenseComparisonStatisticsRequest,
  type IExpensePlanBankAccountsResponse,
  type IExpensePlanCategories,
  type IExpensePlanIntervals,
  type IExpensePlanPeriods,
  type IExpensePlans,
  type IExpensePlanTransactionsResponse,
  type IExtendedExpensePlan,
  type IGetCustomTransactionCategoryRequest,
  type IGetCustomTransactionCategoryResponse,
  type IGetExpensePlanPeriods,
  type IGetExpensePlanRequest,
  type IGetExpensePlansRequest,
  type IGetExpensePlanTransactions,
  type IGetSavingsPlanPeriods,
  type IGetSavingsPlansRequest,
  type IIncrementSavingsPlanAchievedAmountRequest,
  type IInitiateConsentRequest,
  type IInitiateConsentResponse,
  type IMarkNewAccountsAsSeenRequest,
  type IModifyAccountsRequest,
  type IRemoveAccountRequest,
  type IRemoveBankRequest,
  type IRemoveCustomTransactionCategoryRequest,
  type IRemoveExpensePlanRequest,
  type IRemoveSavingsPlanRequest,
  type ISavingsPlanBankAccountsResponse,
  type ISavingsPlanPeriods,
  type ISavingsPlans,
  type ISetAccountAsMainRequest,
  type IToggleCustomerBudgetPreferencesRequest,
  type ITransaction,
  type ITransactionModificationActionCategories,
  type ITransactionRequest,
  type TCreateSavingsPlanRequest,
  type TModifySavingsPlanRequest,
} from "./types";

export const initiateConsent = async (data: IInitiateConsentRequest) =>
  (
    await budgetApiV2.post<IInitiateConsentResponse>(
      `/consents/${data.bankId}/initiate`,
      {
        agreedToBudgetRules: data.agreedToBudgetRules,
      },
    )
  ).data;

export const acquireConsent = async (data: IAcquireConsentRequest) =>
  await budgetApi.post(`/consents/${data.ledgerConsentId}/acquire`);

export const getBudgetServiceMetadata = async () =>
  (
    await budgetApi.get<IBudgetServiceMetadata>(
      "/summaries/retrieve-service-metadata",
    )
  ).data;

export const getBudgetMetadata = async () =>
  (await budgetApi.get<IBudgetMetadata>("/summaries/retrieve-metadata")).data;

export const getBudgetTransactionMetadata = async (
  data: IBudgetTransactionMetadataRequest,
) =>
  (
    await budgetApi.get<IBudgetTransactionMetadata>(
      "/summaries/retrieve-transaction-metadata",
      {
        params: {
          bankAccountId:
            data.bankAccountId === 0 ? undefined : data.bankAccountId,
        },
      },
    )
  ).data;

export const getBudgetSummary = async () =>
  (await budgetApi.get<IBudgetSummary>("/bank-accounts/retrieve-summary")).data;

export const getBudgetSettings = async () =>
  (await budgetApi.get<IBudgetSettings>("/settings/retrieve")).data;

export const getBudgetBalanceStatistics = async (
  data: IBudgetBalanceStatisticsRequest,
) =>
  (
    await budgetApi.get<IBudgetBalanceStatistics>(
      "/statistics/calculate-balance-statistics",
      {
        params: {
          date: format(data.date, "yyyy-MM-dd"),
          bankAccountId: data.bankAccountId,
        },
      },
    )
  ).data;

export const getBankAccountFilter = async () =>
  (
    await budgetApi.get<IBankAccountsFilterResponse>(
      "/bank-accounts/retrieve-filter",
    )
  ).data;

export const getBudgetWeeklyExpenseStatistics = async (
  data: IBudgetWeeklyExpenseStatisticsRequest,
) =>
  (
    await budgetApi.get<IBudgetWeeklyExpenseStatistics>(
      "/statistics/calculate-week-expense-statistics",
      {
        params: {
          dateFrom: format(data.dateFrom, "yyyy-MM-dd"),
          bankAccountId: data.bankAccountId,
        },
      },
    )
  ).data;

export const getBudgetFinanceBreakdown = async (
  data: IBudgetFinanceBreakdownRequest,
) =>
  (
    await budgetApi.get<IBudgetFinanceBreakdown>(
      "/statistics/calculate-finance-breakdown",
      {
        params: {
          dateFrom: format(data.dateFrom, "yyyy-MM-dd"),
          dateTo: format(data.dateTo, "yyyy-MM-dd"),
          bankAccountId: data.bankAccountId,
          ...(!isNullOrUndefined(data.maxCategories)
            ? { maxCategories: data.maxCategories }
            : {}),
        },
      },
    )
  ).data;

export const getBudgetTransactions = async (
  data: IBudgetTransactionsRequest,
) => {
  if (
    data.overrideEarningsCategoryId &&
    data.transactionCategorySlug === "earnings"
  ) {
    data.transactionCategorySlug = undefined;
    data.transactionTypeId = TransactionType.Debit;
  }

  const response = await budgetApiV2.get<IBudgetTransactions>(
    "/transactions/retrieve",
    {
      params: {
        dateFilterType: data.dateFilterType,
        periodFrom: data.periodFrom && format(data.periodFrom, "yyyy-MM-dd"),
        periodTo: data.periodTo && format(data.periodTo, "yyyy-MM-dd"),
        date: data.date && format(data.date, "yyyy-MM-dd"),
        bankAccountId: data.bankAccountId,
        transactionCategorySlug: data.transactionCategorySlug,
        transactionTypeId: data.transactionTypeId,
        searchText: data.searchText,
        respectCustomerPreference: true,
      },
    },
  );
  return response.data;
};

export const getBudgetTransactionAvailableCategories = async (
  data: IBudgetTransactionAvailableCategoriesRequest,
) =>
  (
    await budgetApi.get<IBudgetTransactionAvailableCategories>(
      `/transactions/${data.bankAccountTransactionId}/retrieve-available-categories`,
    )
  ).data;

export const editTransactionCategory = async (
  data: IEditTransactionCategoryRequest,
) => {
  const response = await budgetApiV2.patch(
    `/transactions/${data.bankAccountTransactionId}/modify-category`,
    {
      transactionCategoryId: data.transactionCategoryId,
      transactionSubcategoryId: data.transactionSubcategoryId,
      categoryModificationAction: data.categoryModificationAction,
    },
  );

  // After creating/updating expense plan (or changing transaction categories with expense plans),
  // a backend job runs which updates expense plan data, we have to wait for it
  await sleep(500);

  return response;
};

export const getTransactionModificationActionCategories = async () =>
  (
    await budgetApiV2.get<ITransactionModificationActionCategories>(
      "/transactions/retrieve-categories-modification-actions",
    )
  ).data;

export const setAccountAsMain = async (data: ISetAccountAsMainRequest) =>
  await budgetApi.patch(`/bank-accounts/${data.bankAccountId}/set-as-main`);

export const modifyAccounts = async (data: IModifyAccountsRequest) =>
  await budgetApi.patch(`/bank-accounts/modify`, data);

export const getNewAccounts = async () =>
  (await budgetApi.get<IBudgetNewAccounts>(`/bank-accounts/retrieve-new`)).data;

export const markNewAccountsAsSeen = async (
  data: IMarkNewAccountsAsSeenRequest,
) => await budgetApi.patch(`/bank-accounts/mark-as-seen`, data);

export const removeAccount = async (data: IRemoveAccountRequest) =>
  await budgetApi.delete(`/bank-accounts/${data.bankAccountId}`);

export const removeBank = async (data: IRemoveBankRequest) =>
  await budgetApi.delete(`/banks/${data.bankId}`);

export const getAvailableBanks = async () =>
  (await budgetApi.get<IAvailableBanks>("/banks/retrieve-available")).data;

export const getSavingsPlans = async (data: IGetSavingsPlansRequest) =>
  (
    await budgetApiV2.get<ISavingsPlans>("/savings-plans/retrieve-plans", {
      params: data,
    })
  ).data;

export const retrieveSavingsPlanBankAccounts = async () =>
  (
    await budgetApi.get<ISavingsPlanBankAccountsResponse>(
      "/savings-plans/retrieve-bank-accounts",
    )
  ).data;

export const createSavingsPlan = async (data: TCreateSavingsPlanRequest) =>
  await budgetApiV2.post("/savings-plans/create", data);

export const modifySavingsPlan = async (data: TModifySavingsPlanRequest) =>
  await budgetApiV2.put(`/savings-plans/${data.savingsPlanId}/modify`, {
    planName: data.planName,
    planAmount: data.planAmount,
    endDate: data.endDate,
    savingsPlanDataProviderTypeId: data.savingsPlanDataProviderTypeId,
    ...(data.savingsPlanDataProviderTypeId ===
      SavingsPlanDataProviderType.BankAccountBalance && {
      bankAccountId: data.bankAccountId,
    }),
    ...(data.savingsPlanDataProviderTypeId ===
      SavingsPlanDataProviderType.Manual && {
      achievedAmount: data.achievedAmount,
    }),
  });

export const archiveSavingsPlan = async (data: IArchiveSavingsPlanRequest) =>
  await budgetApi.patch(`/savings-plans/${data.savingsPlanId}/archive`);

export const unarchiveSavingsPlan = async (data: IArchiveSavingsPlanRequest) =>
  await budgetApi.patch(`/savings-plans/${data.savingsPlanId}/unarchive`);

export const getSavingsPlanPeriods = async (data: IGetSavingsPlanPeriods) =>
  (
    await budgetApi.get<ISavingsPlanPeriods>(
      `/savings-plans/${data.savingsPlanId}/retrieve-periods`,
      {
        params: {
          stepBack: data.stepBack,
          elementCount: data.elementCount,
        },
      },
    )
  ).data;

export const incrementSavingsPlanAchievedAmount = async (
  data: IIncrementSavingsPlanAchievedAmountRequest,
) =>
  await budgetApi.patch(
    `/savings-plans/${data.savingsPlanId}/increment-achieved-amount`,
    { incrementAmount: data.incrementAmount },
  );

export const removeSavingsPlan = async (data: IRemoveSavingsPlanRequest) =>
  await budgetApi.delete(`/savings-plans/${data.savingsPlanId}`);

export const getExpensePlans = async (data: IGetExpensePlansRequest) =>
  (
    await budgetApiV2.get<IExpensePlans>("/expense-plans/retrieve-plans", {
      params: data,
    })
  ).data;

export const getExpensePlan = async (data: IGetExpensePlanRequest) =>
  (
    await budgetApi.get<IExtendedExpensePlan>(
      `/expense-plans/${data.expensePlanId}/retrieve`,
    )
  ).data;

export const getExpensePlanIntervals = async () =>
  (
    await budgetApi.get<IExpensePlanIntervals>(
      "/expense-plans/retrieve-intervals",
    )
  ).data;

export const getExpensePlanCategories = async () =>
  (
    await budgetApi.get<IExpensePlanCategories>(
      "/expense-plans/retrieve-categories",
    )
  ).data;

export const getExpensePlanTransactions = async (
  data: IGetExpensePlanTransactions,
) =>
  (
    await budgetApiV2.get<IExpensePlanTransactionsResponse>(
      `/expense-plans/${data.expensePlanId}/retrieve-transactions`,
      {
        params: {
          pageIndex: data.pageIndex,
          pageSize: data.pageSize,
        },
      },
    )
  ).data;

export const getExpensePlanPeriods = async (data: IGetExpensePlanPeriods) =>
  (
    await budgetApiV2.get<IExpensePlanPeriods>(
      `/expense-plans/${data.expensePlanId}/retrieve-periods`,
      {
        params: {
          stepBack: data.stepBack,
          elementCount: data.elementCount,
        },
      },
    )
  ).data;

export const archiveExpensePlan = async (data: IArchiveExpensePlanRequest) =>
  await budgetApi.patch(`/expense-plans/${data.expensePlanId}/archive`);

export const unarchiveExpensePlan = async (data: IArchiveExpensePlanRequest) =>
  await budgetApi.patch(`/expense-plans/${data.expensePlanId}/unarchive`);

export const createExpensePlan = async (data: ICreateExpensePlanRequest) => {
  const response = await budgetApi.post("/expense-plans/create", data);

  // After creating/updating expense plan, a backend job runs which updates expense plan data, we have to wait for it
  await sleep(1500);

  return response;
};

export const editExpensePlan = async (data: IEditExpensePlanRequest) => {
  const response = await budgetApi.patch(
    `/expense-plans/${data.expensePlanId}/modify`,
    {
      planName: data.planName,
      planAmount: data.planAmount,
      bankAccountId: data.bankAccountId,
    },
  );

  // After creating/updating expense plan, a backend job runs which updates expense plan data, we have to wait for it
  await sleep(1500);

  return response;
};

export const removeExpensePlan = async (data: IRemoveExpensePlanRequest) =>
  await budgetApi.delete(`/expense-plans/${data.expensePlanId}/remove`);

export const getTransaction = async (data: ITransactionRequest) =>
  (
    await budgetApiV2.get<ITransaction>(
      `/transactions/${data.bankAccountTransactionId}/retrieve`,
    )
  ).data;

export const getDetailedBalanceStatistics = async (
  data: IDetailedBalanceStatisticsRequest,
) => {
  const response = await budgetApi.get<IDetailedBalanceStatistics>(
    "/statistics/calculate-detailed-balance-statistics",
    {
      params: {
        date: data.date ? format(data.date, "yyyy-MM-dd") : undefined,
        bankAccountId: data.bankAccountId,
      },
    },
  );

  return {
    ...response.data,
    interval: data.selectedInterval,
  };
};

export const getExpensePlanBankAccounts = async () =>
  (
    await budgetApi.get<IExpensePlanBankAccountsResponse>(
      "/expense-plans/retrieve-bank-accounts",
    )
  ).data;

export const getExpenseComparisonStatisticsPartners = async (
  data: IExpenseComparisonStatisticsPartnersRequest,
) =>
  (
    await budgetApi.get<IExpenseComparisonStatisticsPartners>(
      "/statistics/calculate-expense-comparison-statistics/partners",
      {
        params: {
          date: format(data.date, "yyyy-MM-dd"),
          elementsCount: data.elementsCount,
          bankAccountId: data.bankAccountId,
          transactionCategoryId: data.transactionCategoryId,
        },
      },
    )
  ).data;

export const getExpenseComparisonStatisticsExpenseCategories = async () =>
  (
    await budgetApi.get<IExpenseComparisonStatisticsExpenseCategories>(
      "/statistics/calculate-expense-comparison-statistics/expense-categories",
    )
  ).data;

export const getExpenseComparisonStatistics = async (
  data: IExpenseComparisonStatisticsRequest,
) =>
  (
    await budgetApi.get<IExpenseComparisonStatistics>(
      "/statistics/calculate-expense-comparison-statistics",
      {
        params: {
          date: format(data.date, "yyyy-MM-dd"),
          elementsCount: data.elementsCount,
          bankAccountId: data.bankAccountId,
          transactionCategoryId: data.transactionCategoryId,
          partnerName: data.partnerName,
        },
      },
    )
  ).data;

export const createCustomTransactionCategory = async (
  data: ICreateTransactionCategoryRequest,
) => (await budgetApi.post<void>("/transaction-categories", data)).data;

export const editCustomTransactionCategory = async (
  data: IEditCustomTransactionCategoryRequest,
) =>
  (
    await budgetApi.patch<void>(
      `/transaction-categories/${data.transactionCategoryId}`,
      {
        categoryName: data.categoryName,
      },
    )
  ).data;

export const getCustomTransactionCategory = async (
  data: IGetCustomTransactionCategoryRequest,
) =>
  (
    await budgetApi.get<IGetCustomTransactionCategoryResponse>(
      `/transaction-categories/${data.transactionCategoryId}`,
    )
  ).data;

export const removeCustomTransactionCategory = async (
  data: IRemoveCustomTransactionCategoryRequest,
) =>
  (
    await budgetApi.delete<void>(
      `/transaction-categories/${data.transactionCategoryId}`,
      {
        params: {
          transactionCategoryDeletionAction: data.removeType,
          newTransactionCategoryId: data.newTransactionCategoryId,
        },
      },
    )
  ).data;

export const toggleCustomerBudgetPreferences = async (
  data: IToggleCustomerBudgetPreferencesRequest,
) =>
  await budgetApi.patch(
    `/customer-preferences/${data.customerPreferenceTypeId}`,
    { isEnabled: data.isEnabled },
  );

export const getCustomerBudgetPreferences = async () => {
  const response = (
    await budgetApi.get<ICustomerBudgetPreferences>(
      `/summaries/customer-preference-metadata`,
    )
  ).data;

  return Object.fromEntries(
    response.customerPreferences.map(
      ({ customerPreferenceTypeId, isEnabled }) => [
        customerPreferenceTypeId,
        isEnabled,
      ],
    ),
  ) as Record<CustomerPreferenceType, boolean>;
};
